import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { DollarSign, Zap, Mail, X, Lock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sticky "Buy Now" bar that appears after the user scrolls past the hero.
 * Click → email modal → straight to PayPal checkout.
 * Built for impulse $1/$19 conversion lift on cold traffic.
 */
export const StickyBuyBar = () => {
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState('basic');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      // Appear after scrolling 60% of viewport height (past hero)
      const threshold = window.innerHeight * 0.6;
      setVisible(window.scrollY > threshold);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openModal = (tier) => {
    setSelectedTier(tier);
    setShowModal(true);
  };

  const proceedToPayment = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/success`;
      const cancelUrl = `${window.location.origin}/`;
      const amount = selectedTier === 'pro' ? 19.0 : 1.0;
      const res = await axios.post(`${API}/create-payment`, {
        return_url: returnUrl,
        cancel_url: cancelUrl,
        amount,
        tier: selectedTier,
        customer_email: email,
      });
      if (res.data.approval_url) {
        localStorage.setItem('customer_email', email);
        window.location.href = res.data.approval_url;
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Quick buy error:', err);
      }
      toast.error('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (dismissed) return null;

  return (
    <>
      {/* The sticky bar itself */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-500 ease-out ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
        data-testid="sticky-buy-bar"
      >
        <div className="bg-black/95 backdrop-blur-md border-t-2 border-green-500/60 shadow-[0_-8px_30px_rgba(34,197,94,0.25)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 rounded-full whitespace-nowrap">
                <Lock className="w-3.5 h-3.5 text-green-500" />
                <span className="text-green-400 text-xs font-semibold">SSL Secured</span>
              </div>
              <p className="text-green-300 text-sm font-mono truncate">
                Start earning today — choose your tier:
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-1 sm:flex-none justify-end">
              <Button
                onClick={() => openModal('basic')}
                disabled={loading}
                className="btn-secondary px-4 sm:px-6 py-5 text-sm sm:text-base whitespace-nowrap"
                data-testid="sticky-buy-basic"
              >
                <DollarSign className="mr-1 w-4 h-4" />
                Basic <span className="ml-1 font-bold">$1</span>
              </Button>
              <Button
                onClick={() => openModal('pro')}
                disabled={loading}
                className="btn-primary px-4 sm:px-6 py-5 text-sm sm:text-base whitespace-nowrap pulse-glow"
                data-testid="sticky-buy-pro"
              >
                <Zap className="mr-1 w-4 h-4" />
                Pro <span className="ml-1 font-bold">$19</span>
              </Button>
              <button
                onClick={() => setDismissed(true)}
                className="text-green-500/60 hover:text-green-400 p-1"
                aria-label="Dismiss"
                data-testid="sticky-buy-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Email collection modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="quick-buy-modal-overlay"
        >
          <Card className="card-tech p-8 max-w-md mx-4 w-full relative" data-testid="quick-buy-modal">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-green-400 hover:text-green-300"
              data-testid="quick-buy-close"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center mb-6">
              <Mail className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-green-400">
                {selectedTier === 'pro' ? 'Get Pro Edition - $19' : 'Get Basic Edition - $1'}
              </h3>
              <p className="text-green-300 mt-2 text-sm">
                Enter your email to receive access instructions after payment.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-green-400 text-sm font-semibold mb-2">
                  Your Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-black/50 border-green-500/30 text-green-300 h-12"
                  onKeyDown={(e) => e.key === 'Enter' && proceedToPayment()}
                  autoFocus
                  data-testid="quick-buy-email-input"
                />
                <p className="text-green-500/60 text-xs mt-1">
                  Works with any email, even if you pay by credit/debit card.
                </p>
              </div>

              <Button
                onClick={proceedToPayment}
                disabled={loading}
                className="btn-primary w-full py-5 text-lg"
                data-testid="quick-buy-proceed"
              >
                {loading ? 'Processing...' : 'Continue to Payment'}
              </Button>
            </div>

            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-green-500/20">
              <Lock className="w-3 h-3 text-green-500" />
              <span className="text-green-500/70 text-[11px]">
                Secured by 256-bit SSL · PayPal Verified
              </span>
            </div>
          </Card>
        </div>
      )}
    </>
  );
};

export default StickyBuyBar;
